import { useCallback, useEffect, useRef, useState } from 'react';
import { useNavigate } from 'react-router-dom';
import OnboardingAuthLayout from '../components/onboarding/OnboardingAuthLayout';
import PrimaryButton from '../components/onboarding/PrimaryButton';
import LegalDocModal from '../components/onboarding/LegalDocModal';
import TurnstileWidget from '../components/TurnstileWidget';
import { setGlobalState } from '../components/AppContext';
import {
  isTurnstileEnabled,
  logTurnstileError,
  TURNSTILE_USER_ERROR_MESSAGE,
} from '../utils/turnstile';
import { captureSignupUtmFromUrl, getStoredUtm } from '../utils/signupUtm';
import {
  identifyCustomerIoSignupProspect,
  trackCustomerIoFormStarted,
  trackCustomerIoFormSubmitted,
  trackCustomerIoSignupFailed,
} from '../utils/customerio';
import {
  SIGNUP_PASSWORD_REQUIREMENTS,
  getSignupPasswordError,
  getSignupPasswordRequirements,
  isValidEmail,
  isValidSignupPassword,
  normalizeEmail,
} from '../utils/onboardingValidation';
import { getCallableFriendlyErrorMessage } from '../utils/callableErrors';
import { getPinnedOrDirectCallable } from '../utils/pinnedCallables';
import { getCmsAppUrl } from '../utils/cmsAppUrl';
import { trackOpenAiOnboardingStep } from '../utils/trackOpenAiOnboardingStep';
import { trackOpenAiRegistrationCompleted } from '../utils/trackOpenAiRegistration';

function OnboardingSignup() {
  const navigate = useNavigate();
  const turnstileRef = useRef(null);

  const [firstName, setFirstName] = useState('');
  const [lastName, setLastName] = useState('');
  const [email, setEmail] = useState('');
  const [password, setPassword] = useState('');
  const [companyName, setCompanyName] = useState('');
  const [termsAccepted, setTermsAccepted] = useState(false);
  const [emailError, setEmailError] = useState('');
  const [passwordError, setPasswordError] = useState('');
  const [formError, setFormError] = useState('');
  const [loading, setLoading] = useState(false);
  const [trackedStart, setTrackedStart] = useState(false);
  const [showFillTooltip, setShowFillTooltip] = useState(false);
  const [legalDoc, setLegalDoc] = useState(null);

  const startOnboardingSignup = getPinnedOrDirectCallable(
    'startOnboardingSignup'
  );

  useEffect(() => {
    captureSignupUtmFromUrl();
  }, []);

  useEffect(() => {
    const previousBody = document.body.style.cssText;
    const previousHtml = document.documentElement.style.cssText;
    const root = document.getElementById('root');
    const previousRoot = root ? root.style.cssText : '';
    document.documentElement.style.cssText =
      'height:100%;overflow:hidden;';
    document.body.style.cssText =
      'margin:0;padding:0;height:100%;overflow:hidden;display:block;background:#ffffff;background-image:none;';
    if (root) {
      root.style.cssText = 'height:100%;overflow:hidden;';
    }
    return () => {
      document.documentElement.style.cssText = previousHtml;
      document.body.style.cssText = previousBody;
      if (root) {
        root.style.cssText = previousRoot;
      }
    };
  }, []);

  useEffect(() => {
    if (!showFillTooltip) {
      return undefined;
    }
    const timer = setTimeout(() => setShowFillTooltip(false), 2800);
    return () => clearTimeout(timer);
  }, [showFillTooltip]);

  const handleTurnstileError = useCallback((errorCode) => {
    logTurnstileError(errorCode);
  }, []);

  const signupFormTraits = (overrides = {}) => ({
    signup_source: 'signup',
    plan_type: 'free_trial',
    first_name: firstName.trim() || undefined,
    last_name: lastName.trim() || undefined,
    company_name: companyName.trim() || undefined,
    ...overrides,
  });

  const markFormStarted = () => {
    if (trackedStart) {
      return;
    }
    setTrackedStart(true);
    if (typeof gtag !== 'undefined') {
      gtag('event', 'form_start', {
        event_category: 'engagement',
        event_label: 'onboarding_signup_form_started',
      });
    }
  };

  const trackOnboardingFormStarted = (emailValue) => {
    trackCustomerIoFormStarted(
      signupFormTraits({
        event_label: 'onboarding_signup_form_started',
      }),
      emailValue
    );
  };

  const emailIsValid = isValidEmail(email);
  const passwordIsValid = isValidSignupPassword(password);
  const passwordRequirements = getSignupPasswordRequirements(password);
  const formComplete =
    Boolean(firstName.trim()) &&
    Boolean(lastName.trim()) &&
    emailIsValid &&
    passwordIsValid &&
    termsAccepted;
  const canSubmit = formComplete && !loading;

  const handleSubmit = async (event) => {
    event.preventDefault();
    setFormError('');
    setEmailError('');
    setPasswordError('');

    if (!formComplete) {
      setShowFillTooltip(true);
      return;
    }
    if (loading) {
      return;
    }
    setShowFillTooltip(false);

    const trimmedFirst = firstName.trim();
    const trimmedLast = lastName.trim();
    const trimmedEmail = normalizeEmail(email);
    const trimmedCompany = companyName.trim();

    if (!trimmedFirst || !trimmedLast) {
      setFormError('First name and last name are required.');
      return;
    }
    if (!isValidEmail(trimmedEmail)) {
      setEmailError('Enter a valid email address.');
      return;
    }
    if (!isValidSignupPassword(password)) {
      setPasswordError(getSignupPasswordError(password));
      return;
    }
    if (!termsAccepted) {
      setFormError('Accept the Terms of Service and Privacy Policy to continue.');
      return;
    }

    let turnstileToken;
    if (isTurnstileEnabled()) {
      try {
        turnstileToken = await turnstileRef.current?.getToken();
      } catch (turnstileError) {
        console.error('Turnstile verification error:', turnstileError);
        setFormError(TURNSTILE_USER_ERROR_MESSAGE);
        return;
      }
      if (!turnstileToken) {
        setFormError(TURNSTILE_USER_ERROR_MESSAGE);
        return;
      }
    }

    const submittedTraits = signupFormTraits({
      first_name: trimmedFirst,
      last_name: trimmedLast,
      company_name: trimmedCompany || undefined,
      event_label: 'onboarding_signup_form_submitted',
    });
    trackOnboardingFormStarted(trimmedEmail);
    identifyCustomerIoSignupProspect(trimmedEmail, submittedTraits);
    trackCustomerIoFormSubmitted(submittedTraits, trimmedEmail);

    setLoading(true);
    if (typeof gtag !== 'undefined') {
      gtag('event', 'form_submit', {
        event_category: 'engagement',
        event_label: 'onboarding_signup_form_submitted',
      });
    }

    try {
      const result = await startOnboardingSignup({
        firstName: trimmedFirst,
        lastName: trimmedLast,
        email: trimmedEmail,
        password,
        companyName: trimmedCompany,
        termsAccepted: true,
        turnstileToken,
        utm: getStoredUtm(),
      });

      if (result?.data?.code !== 200) {
        throw new Error(result?.data?.message || 'Signup failed');
      }

      setGlobalState('email', trimmedEmail);
      setGlobalState('firstName', trimmedFirst);
      setGlobalState('lastName', trimmedLast);
      setGlobalState('companyName', trimmedCompany);

      trackOpenAiRegistrationCompleted({ eventId: trimmedEmail });
      trackOpenAiOnboardingStep(1, 'completed');

      navigate('/verify-email', {
        state: {
          email: trimmedEmail,
          debugVerificationCode: result.data.debugVerificationCode,
        },
      });
    } catch (err) {
      console.error('Onboarding signup error:', err);
      if (
        err?.code === 'functions/permission-denied' ||
        err?.code === 'permission-denied'
      ) {
        setFormError(TURNSTILE_USER_ERROR_MESSAGE);
        trackCustomerIoSignupFailed(
          {
            failure_stage: 'onboarding_signup',
            error_reason: 'turnstile_verification_failed',
            signup_source: 'signup',
            plan_type: 'free_trial',
          },
          trimmedEmail
        );
      } else if (
        err?.code === 'functions/already-exists' ||
        err?.code === 'already-exists'
      ) {
        setFormError(
          'An account with this email already exists. Log in instead.'
        );
      } else {
        setFormError(
          getCallableFriendlyErrorMessage(
            err,
            'We could not create your account. Please try again.'
          )
        );
        trackCustomerIoSignupFailed(
          {
            failure_stage: 'onboarding_signup',
            error_reason: err?.message || 'signup_failed',
            signup_source: 'signup',
            plan_type: 'free_trial',
          },
          trimmedEmail
        );
      }
    } finally {
      setLoading(false);
    }
  };

  return (
    <OnboardingAuthLayout
      showProgress
      activeStep={1}
      heading="Create an account to publish killer content without a deployment"
      subtext="Start your 14-day free trial — set a password and we'll send a verification code to your work email."
    >
      <form className="onboarding-form" onSubmit={handleSubmit} noValidate>
        <div className="onboarding-form-row">
          <div className="onboarding-field">
            <label className="onboarding-label" htmlFor="ob-first-name">
              First name
            </label>
            <input
              id="ob-first-name"
              name="firstName"
              className="onboarding-input"
              autoComplete="given-name"
              value={firstName}
              onChange={(e) => {
                markFormStarted();
                setFirstName(e.target.value);
                setFormError('');
              }}
              required
            />
          </div>
          <div className="onboarding-field">
            <label className="onboarding-label" htmlFor="ob-last-name">
              Last name
            </label>
            <input
              id="ob-last-name"
              name="lastName"
              className="onboarding-input"
              autoComplete="family-name"
              value={lastName}
              onChange={(e) => {
                markFormStarted();
                setLastName(e.target.value);
                setFormError('');
              }}
              required
            />
          </div>
        </div>

        <div className="onboarding-form-row">
          <div className="onboarding-field">
            <label className="onboarding-label" htmlFor="ob-work-email">
              Work email
            </label>
            <input
              id="ob-work-email"
              name="email"
              type="email"
              className={`onboarding-input ${emailError ? 'is-invalid' : ''}`}
              autoComplete="email"
              placeholder="jane@acme.com"
              value={email}
              onChange={(e) => {
                setEmail(e.target.value);
                markFormStarted();
                setEmailError('');
                setFormError('');
              }}
              onBlur={(e) => {
                if (e.target.value && !isValidEmail(e.target.value)) {
                  setEmailError('Enter a valid email address.');
                }
                trackOnboardingFormStarted(e.target.value);
              }}
              aria-invalid={emailError ? 'true' : undefined}
              aria-describedby="ob-work-email-help ob-work-email-error"
              required
            />
            <p id="ob-work-email-help" className="onboarding-helper">
              We'll send a verification code to this address.
            </p>
            {emailError ? (
              <p id="ob-work-email-error" className="onboarding-error" role="alert">
                {emailError}
              </p>
            ) : null}
          </div>
          <div className="onboarding-field">
            <label className="onboarding-label" htmlFor="ob-company">
              Company name (optional)
            </label>
            <input
              id="ob-company"
              name="companyName"
              className="onboarding-input"
              autoComplete="organization"
              value={companyName}
              onChange={(e) => {
                markFormStarted();
                setCompanyName(e.target.value);
              }}
            />
          </div>
        </div>

        <div className="onboarding-field">
          <label className="onboarding-label" htmlFor="ob-password">
            Set password
          </label>
          <input
            id="ob-password"
            name="password"
            type="password"
            className={`onboarding-input ${passwordError ? 'is-invalid' : ''}`}
            autoComplete="new-password"
            value={password}
            onChange={(e) => {
              markFormStarted();
              setPassword(e.target.value);
              setPasswordError('');
              setFormError('');
            }}
            onBlur={() => {
              if (password && !isValidSignupPassword(password)) {
                setPasswordError(getSignupPasswordError(password));
              }
            }}
            aria-invalid={passwordError ? 'true' : undefined}
            aria-describedby="ob-password-reqs ob-password-error"
            required
          />
          <ul id="ob-password-reqs" className="onboarding-password-reqs">
            {SIGNUP_PASSWORD_REQUIREMENTS.map((requirement) => (
              <li
                key={requirement.id}
                className={
                  passwordRequirements[requirement.id] ? 'is-met' : undefined
                }
              >
                {requirement.label}
              </li>
            ))}
          </ul>
          {passwordError ? (
            <p id="ob-password-error" className="onboarding-error" role="alert">
              {passwordError}
            </p>
          ) : null}
        </div>

        <label className="onboarding-terms">
          <span className="onboarding-checkbox">
            <input
              type="checkbox"
              checked={termsAccepted}
              onChange={(e) => {
                setTermsAccepted(e.target.checked);
                setFormError('');
              }}
            />
            <span className="onboarding-checkbox-box" aria-hidden="true" />
          </span>
          <span>
            I agree to the{' '}
            <button
              type="button"
              className="onboarding-legal-link"
              onClick={(event) => {
                event.preventDefault();
                event.stopPropagation();
                setLegalDoc('terms');
              }}
            >
              Terms of Service
            </button>{' '}
            and{' '}
            <button
              type="button"
              className="onboarding-legal-link"
              onClick={(event) => {
                event.preventDefault();
                event.stopPropagation();
                setLegalDoc('privacy');
              }}
            >
              Privacy Policy
            </button>
            .
          </span>
        </label>

        <TurnstileWidget
          ref={turnstileRef}
          onError={handleTurnstileError}
          className="onboarding-turnstile"
        />

        {formError ? (
          <p className="onboarding-error" role="alert">
            {formError}
          </p>
        ) : null}

        <PrimaryButton
          type="submit"
          disabled={!canSubmit}
          loading={loading}
          disabledTooltip={
            formComplete ? undefined : 'The form must be filled out'
          }
          showDisabledTooltip={showFillTooltip && !formComplete}
          onShowDisabledTooltip={() => setShowFillTooltip(true)}
        >
          {loading ? 'Creating account…' : 'Create Account'}
        </PrimaryButton>

        <p className="onboarding-switch-prompt">
          Already have an account?{' '}
          <a href={getCmsAppUrl()} rel="noopener noreferrer">
            Log In
          </a>
        </p>
      </form>
      <LegalDocModal doc={legalDoc} onClose={() => setLegalDoc(null)} />
    </OnboardingAuthLayout>
  );
}

export default OnboardingSignup;
